import { ApolloQueryResult, OperationVariables } from '@apollo/client';
import { isNil } from 'lodash';
import React, { ComponentType, Dispatch, SetStateAction, useCallback, useContext, useMemo } from 'react';
import { Resource as ProductionResource, ResourceInput } from 'sr-types/lib/production/v1/graphql';
import { Reference } from 'sr-types/lib/resource/v1/graphql';
import { Resource } from 'sr-types/lib/search/v1/graphql';
import { ShortlistInput } from 'sr-types/lib/shortlist/v1/graphql';
import Attachments from '../common/attachments/Attachments';
import { EnvironmentPermissionContext } from '../common/auth/EnvironmentPermissionContext';
import { SECURITY_ACTIONS } from '../common/auth/Security';
import type { MainSidePanelProps, SecuredSidePanelDef } from '../common/components/layout/LayoutUtils';
import { getEntityShareSettingsPanelConfig } from '../common/components/sidePanelConfigurations/ShareSettingsPanel';
import { CONSTANTS_ID, SIDE_PANEL_IDS, SIZES, STATE_CONSTANTS } from '../common/constants/constants';
import Validator from '../common/form/Validator';
import { useMutationWithContext } from '../common/hooks/useMutationWithContext';
import I18n from '../common/i18n/I18n';
import { Icons } from '../common/icons/Icons';
import type { DataGridCustomRowAction } from '../common/list/DataGrid/types';
import type { DataListActionHandlers } from '../common/list/DataList/types';
import Notes from '../common/notes/Notes';
import { ShareableEntity } from '../common/share';
import { failedToastMessage, successToastMessage } from '../common/utils/commonUtils';
import useHistory from '../common/utils/useHistory';
import { resourceHiredState } from '../person/mockData';
import { client as productionClient, saveResourceQuery } from '../production/helpers/production';
import { RESOURCE_TYPE } from './common/resourceConstants';
import { client } from './resource';
import ResourceAttributesSidePanel from './ResourceAttributesSidePanel';
import { buildResourceValidationRules } from './resourceValidation';
import { ResourceTemplateFormValues } from './templateResource/CreateResourceFromTemplateForm';

export type StepperResourceEditorProps = {
    productionId: string;
    resourceFormIndex: number;
    setResourceFormIndex: Dispatch<SetStateAction<number>>;
    initialState: ResourceTemplateFormValues;
    setInitialState: Dispatch<SetStateAction<ResourceTemplateFormValues>>;
    resourceEditedIndex: number;
    setResourceEditedIndex: Dispatch<SetStateAction<number>>;
};

export type ResourceAttributesSidePanelProps = {
    entityId: string;
    entityName: string;
    onClose: () => void;
    onSaveEntity: (
        values: ProductionResource & ResourceInput,
        isDeletingAttribute: boolean
    ) => Promise<string | undefined>;
};

export const makeResourceAttributesDataGridAction = (onClick: (row: Resource) => void): DataGridCustomRowAction => {
    const action: DataGridCustomRowAction = {
        securityAction: SECURITY_ACTIONS.EDIT,
        labelToken: 'attributes.title',
        icon: <Icons.ResourceFields fontSize={SIZES.small} />,
        onClick
    };

    return action;
};

export const useOpenResourceSidePanel = ({
    clearShareableEntity,
    setEntityReference,
    setSelectedEntitySecurity,
    setSidePanelOpened
}: {
    clearShareableEntity: () => void;
    setEntityReference: Dispatch<SetStateAction<Reference | undefined>>;
    setSelectedEntitySecurity: Dispatch<SetStateAction<Resource['security'] | undefined>>;
    setSidePanelOpened: Dispatch<SetStateAction<string | undefined>>;
}) =>
    useCallback(
        (item: Resource, panelId: string) => {
            clearShareableEntity();
            setEntityReference(item.reference);
            setSelectedEntitySecurity(item.security);
            setSidePanelOpened(panelId);
        },
        [clearShareableEntity, setEntityReference, setSelectedEntitySecurity, setSidePanelOpened]
    );

export const useOpenResourceAttributesSidePanel = (
    openResourceSidePanel: (item: Resource, panelId: string) => void
): DataListActionHandlers['onCustomAttribute'] =>
    useCallback(
        (item) => {
            openResourceSidePanel(item as Resource, SIDE_PANEL_IDS.attributes);
        },
        [openResourceSidePanel]
    );

export const useGetSidePanels = (
    entityReference: Reference | undefined,
    entitySecurityProviderProps: MainSidePanelProps['entitySecurityProviderProps'],
    onCloseSidePanel?: () => void,
    shareableEntity?: ShareableEntity,
    refetch?: (variables?: Partial<OperationVariables>) => Promise<ApolloQueryResult<any>>,
    isShareEntityLoading: boolean = false,
    onSaveResource?: (input: ResourceInput) => Promise<string | { id?: string } | undefined>,
    AttributesPanelComponent: ComponentType<ResourceAttributesSidePanelProps> = ResourceAttributesSidePanel
) => {
    const { routeParams } = useHistory();
    const [saveResource] = useMutationWithContext(saveResourceQuery, productionClient);
    const showToolTip = Boolean(entityReference && onCloseSidePanel); // We have to show tooltip only in case of lisview.
    const productionId = routeParams.productionId;

    const handleSaveResourceAfterAttributeEdit = useCallback(
        async (updatedValues: ProductionResource & ResourceInput, isDeletingAttribute: boolean) => {
            const successMessageToken = isDeletingAttribute
                ? 'attribute.delete.success.message'
                : 'attributes.save.success.message';
            const failureMessageToken = isDeletingAttribute
                ? 'attribute.delete.failure.message'
                : 'attributes.save.failure.message';

            if (!entityReference?.id) {
                failedToastMessage(failureMessageToken);
                return undefined;
            }

            const input = toInputForSaveResource(entityReference.id, updatedValues);

            if (!productionId) {
                if (!onSaveResource) {
                    failedToastMessage(failureMessageToken);
                    return undefined;
                }

                try {
                    const saveResourceResult = await onSaveResource(input);
                    refetch?.();

                    return typeof saveResourceResult === 'string' ? saveResourceResult : saveResourceResult?.id;
                } catch (error) {
                    failedToastMessage(failureMessageToken);
                    return undefined;
                }
            }

            try {
                const saveResourceResponse = await saveResource({
                    variables: {
                        productionId,
                        input
                    }
                });

                if (saveResourceResponse?.data?.saveResource?.errors) {
                    failedToastMessage(failureMessageToken);
                    return undefined;
                }

                successToastMessage(successMessageToken);
                refetch?.();

                return saveResourceResponse?.data?.saveResource?.id;
            } catch (error) {
                failedToastMessage(failureMessageToken);
                return undefined;
            }
        },
        [entityReference?.id, onSaveResource, productionId, refetch, saveResource]
    );

    const sidePanels = useMemo((): SecuredSidePanelDef[] => {
        return [
            {
                id: SIDE_PANEL_IDS.attributes,
                icon: Icons.ResourceFields,
                label: <I18n token="attributes.title" />,
                tooltip: showToolTip ? <I18n token="attributes.title" /> : undefined,
                disabled: !entityReference?.id,
                securityAction: SECURITY_ACTIONS.EDIT,
                entityName: entitySecurityProviderProps.entityName,
                component: (onClose) => {
                    if (!entityReference?.id) {
                        return null;
                    }

                    return (
                        <AttributesPanelComponent
                            entityId={entityReference.id}
                            entityName={entityReference.label ?? ''}
                            onClose={() => {
                                onCloseSidePanel?.();
                                onClose();
                            }}
                            onSaveEntity={handleSaveResourceAfterAttributeEdit}
                        />
                    );
                }
            },
            {
                id: 'notes',
                icon: Icons.Notes,
                label: <I18n token="notes.title" />,
                tooltip: showToolTip ? <I18n token="notes.close" /> : undefined,
                disabled: !entityReference,
                securityAction: SECURITY_ACTIONS.COMMENT,
                entityName: entitySecurityProviderProps.entityName,
                component: (onClose) => (
                    <Notes
                        id={'notes-resource'}
                        title={<I18n token="notes.panel.title" values={{ entityName: entityReference?.label }} />}
                        entity={entitySecurityProviderProps.entityName}
                        entityReference={entityReference}
                        onClose={() => {
                            onCloseSidePanel && onCloseSidePanel();
                            onClose();
                        }}
                    />
                )
            },
            {
                id: 'attachments',
                icon: Icons.Attachments,
                label: <I18n token="attachments.title" />,
                tooltip: showToolTip ? <I18n token="atttachments.close" /> : undefined,
                disabled: !entityReference,
                securityAction: SECURITY_ACTIONS.COMMENT,
                entityName: entitySecurityProviderProps.entityName,
                component: (onClose) => (
                    <Attachments
                        id={'attachments-resource'}
                        entityReference={entityReference}
                        title={<I18n token="attachments.panel.title" values={{ entityName: entityReference?.label }} />}
                        onClose={() => {
                            onCloseSidePanel && onCloseSidePanel();
                            onClose();
                        }}
                        entity={entitySecurityProviderProps.entityName}
                    />
                )
            },
            ...(shareableEntity || isShareEntityLoading
                ? [
                      {
                          ...getEntityShareSettingsPanelConfig({
                              client: client,
                              shareSettingsParams: {
                                  entityId: entityReference?.id || null,
                                  entity: shareableEntity || null,
                                  title: (
                                      <I18n token="share.panel.title" values={{ entityName: entityReference?.label }} />
                                  ),
                                  isLoading: isShareEntityLoading,
                                  onClose: onCloseSidePanel,
                                  makeShareSettingsScopeId: (entityId) =>
                                      `${entitySecurityProviderProps.entityName}-${entityId}`,
                                  onAfterShareSettingsSave: refetch
                              }
                          }),
                          securityAction: SECURITY_ACTIONS.SHARE,
                          entityName: entitySecurityProviderProps.entityName
                      }
                  ]
                : [])
        ];
    }, [
        entityReference,
        entitySecurityProviderProps.entityName,
        AttributesPanelComponent,
        handleSaveResourceAfterAttributeEdit,
        onCloseSidePanel,
        showToolTip,
        shareableEntity,
        refetch,
        isShareEntityLoading
    ]);

    return sidePanels;
};

export const useGetResourceActionsMenu = ({
    onAdd,
    onAddFromPreview,
    onAddFromTemplate
}: {
    onAdd: () => void;
    onAddFromPreview: () => void;
    onAddFromTemplate: () => void;
}) => {
    const { allowWIP } = useContext(EnvironmentPermissionContext);
    const { routeParams } = useHistory();
    const { productionId } = routeParams;

    const actions = [
        {
            label: <I18n token={'resource.menu.add.custom'} />,
            onClick: onAdd
        },
        ...(allowWIP && productionId
            ? [
                  {
                      label: <I18n token={'resource.menu.add.preview'} />,
                      onClick: onAddFromPreview
                  }
              ]
            : [])
    ];
    return actions;
};

export const updateResourceTemplateValues = (resourceData: Resource) => {
    const { type, subType, about, budgetCategory, resourceCategory, status, role } = resourceData;

    return {
        name: subType || '',
        type,
        subType,
        summary: { about },
        role,
        status,
        budgetCategory,
        resourceCategory,
        attributes: resourceData.attributes,
        associatedFieldDefinitionReferences: resourceData.associatedFieldDefinitionReferences
    };
};

export const EMPTY_SCHEDULE = {
    autoSetDate: false,
    dateRange: {
        dateTimeDuration: {
            unit: 'day',
            value: 1
        },
        durationSpecified: true,
        end: '',
        endTime: '',
        start: '',
        startTime: ''
    }
};

export const COMMON_EMPTY_RESOURCE = {
    type: '',
    role: '',
    name: '',
    status: STATE_CONSTANTS.open,
    budgetCategory: null,
    resourceCategory: null,
    locations: [],
    resourceSchedule: {
        ...EMPTY_SCHEDULE
    }
};

export const EMPTY_RESOURCE = {
    ...COMMON_EMPTY_RESOURCE,
    summary: undefined,
    attributes: undefined,
    associatedFieldDefinitionReferences: undefined
};

export const EMPTY_RESOURCE_TEMPLATE_VALUES = {
    ...COMMON_EMPTY_RESOURCE,
    id: '',
    linkedActivities: []
};

export const EMPTY_RESOURCE_PREVIEW = {
    ...COMMON_EMPTY_RESOURCE,
    name: '',
    subtype: '',
    role: '',
    about: '',
    hiringLead: null,
    hiringTeamMembers: [],
    hiredPerson: null,
    hiredOrganization: null,
    state: resourceHiredState[0]
};

export const EMPTY_RESOURCE_DETAILS = {
    name: '',
    subType: '',
    role: '',
    status: STATE_CONSTANTS.open,
    budgetCategory: null,
    resourceCategory: null,
    locations: [],
    about: ''
};

export const Resource_Field_Names = {
    name: 'name',
    type: 'type',
    resourceCategory: 'resourceCategory',
    role: 'role',
    budgetCategory: 'budgetCategory',
    status: 'status',
    locations: 'locations',
    'summary.about': 'summary.about',
    'resourceSchedule.dateRange.durationSpecified': 'resourceSchedule.dateRange.durationSpecified',
    'resourceSchedule.dateRange': 'resourceSchedule.dateRange',
    'schedule.section': 'schedule.section',
    subType: 'subType',
    'hiring.info.section': 'hiring.info.section',
    acquisitionLead: 'acquisitionLead',
    hiringTeamMembers: 'hiringTeamMembers',
    hiredPerson: 'hiredPerson',
    hiredOrganization: 'hiredOrganization',
    state: 'state'
};

export const toInputForSaveResource = (id: string, entity) => {
    const {
        name,
        type,
        status,
        resourceSchedule,
        attributes,
        associatedFieldDefinitionReferences,
        role,
        budgetCategory,
        locations,
        resourceCategory,
        summary,
        hiredOrganization,
        hiredPerson,
        acquisitionLead,
        hiringTeam,
        hiringTeamMembers,
        subType,
        state,
        proposals,
        orders,
        bookings,
        templateId
    } = entity;

    const resourceInput: ResourceInput = {
        identity: {
            id: id === CONSTANTS_ID.createId ? '' : id
        },
        name,
        type,
        status,
        resourceSchedule,
        attributes,
        associatedFieldDefinitionReferences,
        role,
        budgetCategory,
        locations,
        resourceCategory,
        summary,
        subType,
        state,
        proposals,
        orders,
        bookings,
        ...(templateId ? { templateId } : {})
    };

    if (entity.role) {
        resourceInput.role = entity.role;
    }
    if (entity.resourceCategory?.value || entity.resourceCategory) {
        resourceInput.resourceCategory = entity.resourceCategory.value || entity.resourceCategory;
    }
    if (entity.budgetCategory) {
        resourceInput.budgetCategory = entity.budgetCategory;
    }
    if (entity.locations) {
        resourceInput.locations = entity.locations;
    }
    if (entity.summary) {
        resourceInput.summary = entity.summary;
    }
    if (hiredOrganization) {
        resourceInput.hiredOrganization = hiredOrganization.ref;
    }
    if (hiredPerson) {
        resourceInput.hiredPerson = hiredPerson;
    }
    if (acquisitionLead) {
        resourceInput.acquisitionLead = acquisitionLead;
    }
    if (hiringTeamMembers?.length) {
        resourceInput.hiringTeam = { identity: { id: hiringTeam?.identity?.id || '' }, members: hiringTeamMembers };
    }
    if (isNil(resourceSchedule)) {
        resourceInput.resourceSchedule = { ...EMPTY_SCHEDULE };
    }
    return resourceInput;
};

export const resourceServiceDataToPreview = (resource: ProductionResource, isProduction: boolean = false) => {
    const { resourceCategory, hiringTeam, budgetCategory } = resource;

    return {
        ...resource,
        resourceCategory: resourceCategory ? { value: resourceCategory, lineage: { value: resourceCategory } } : null,
        budgetCategory: budgetCategory || null,
        ...(isProduction ? { hiringTeam: hiringTeam || [], hiringTeamMembers: hiringTeam?.members || [] } : {})
    };
};

export type ResourceInfoForShortlist = {
    resourceInput: ResourceInput;
    savedResourceRef: Reference;
};
export const resourceStateToShortlist = (id: string, resourceInfo: ResourceInfoForShortlist, shortlistData?: any) => {
    const { resourceInput, savedResourceRef } = resourceInfo;
    const savedShortlist: ShortlistInput = {
        name: resourceInput.name,
        identity: { id: id === CONSTANTS_ID.createId ? '' : shortlistData?.shortlist?.identity?.id },
        context: savedResourceRef
    };
    if (id !== CONSTANTS_ID.createId) {
        resourceInput.identity = { id };
    }
    if (resourceInput?.name) {
        savedShortlist.name = resourceInput.name;
    }
    if (resourceInput?.type) {
        savedShortlist.type = resourceInput.type;
    }
    if (resourceInput.role && [RESOURCE_TYPE.cast, RESOURCE_TYPE.crew].includes(resourceInput.type)) {
        savedShortlist.roles = [resourceInput.role];
    }
    if (resourceInput.resourceCategory && ![RESOURCE_TYPE.cast, RESOURCE_TYPE.crew].includes(resourceInput.type)) {
        savedShortlist.categories = [resourceInput.resourceCategory];
    }
    return savedShortlist;
};

export const resourceStaticValidationRules = {
    name: [Validator.RULES.isRequired],
    type: [Validator.RULES.isRequired],
    status: [Validator.RULES.isRequired]
};

const resourceTemplatesStaticValidationRules = {
    name: [Validator.RULES.isRequired],
    type: [Validator.RULES.isRequired]
};

export const resourceFormValidationRules = (hasProductionId: boolean, isCrew: boolean) => {
    if (hasProductionId) {
        return isCrew
            ? { ...resourceStaticValidationRules, role: [Validator.RULES.isRequired] }
            : {
                  ...resourceStaticValidationRules
              };
    } else {
        return { ...resourceTemplatesStaticValidationRules };
    }
};

export const resourcePreviewValidationRules = (isCrewOrCast: boolean) => {
    return {
        ...resourceStaticValidationRules,
        ...(isCrewOrCast ? { role: [Validator.RULES.isRequired] } : {})
    };
};

export const getResourceValidationRules = (isCrewOrCast: boolean, isTemplate: boolean = false) => {
    return buildResourceValidationRules(
        isCrewOrCast,
        isTemplate,
        resourceStaticValidationRules,
        [Validator.RULES.isRequired]
    );
};

export const onSaveResource = async (
    savedResource: any,
    productionId: string,
    saveResource?: (options: { variables: { input: any; productionId: string } }) => Promise<any>,
    productionResourceDetailsRefetch?: () => void,
    onSuccess?: () => void
) => {
    try {
        const saveResourceResponse = await saveResource({
            variables: {
                input: savedResource,
                productionId
            }
        });

        if (saveResourceResponse?.data?.saveResource?.errors) {
            failedToastMessage('resource.save.failure');
        } else {
            successToastMessage('resource.save.success');
            productionResourceDetailsRefetch();
            onSuccess?.();
        }
    } catch (error) {
        failedToastMessage('resource.save.failure');
    }
};

export function sanitizePreviewFormValues(values, prev) {
    return {
        ...values,
        resourceCategory:
            typeof values.resourceCategory === 'string'
                ? values.resourceCategory
                : values.resourceCategory?.value || prev.resourceCategory || '',
        budgetCategory: values.budgetCategory ?? prev.budgetCategory ?? '',
        hiringTeam: values.hiringTeam ?? prev.hiringTeam ?? null,
        acquisitionLead: values.acquisitionLead ?? prev.acquisitionLead ?? null,
        summary: {
            ...prev.summary,
            ...values.summary,
            about: values.about ?? values.summary?.about ?? prev.summary?.about ?? '',
            headline: values.headline ?? values.summary?.headline ?? prev.summary?.headline ?? ''
        },
        type: values.type ?? prev.type ?? '',
        role: values.role ?? prev.role ?? '',
        subType: values.subType ?? values.subtype ?? prev.subType ?? '',
        status: values.status ?? prev.status ?? '',
        locations: values.locations ?? (values.location ? [values.location] : (prev.locations ?? []))
    };
}
